import React, { useState, useEffect, useRef } from 'react';
import '../../styles/date-picker.css';

const DateRangePicker = ({ 
  startDate, 
  endDate, 
  onChange,
  presets = true,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStart, setSelectedStart] = useState(startDate ? new Date(startDate) : null);
  const [selectedEnd, setSelectedEnd] = useState(endDate ? new Date(endDate) : null);
  const [selecting, setSelecting] = useState('start'); // 'start' or 'end'
  
  const pickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getMonthData = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = getDaysInMonth(currentMonth);
    
    let days = [];
    // Add empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    // Add days of month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const handleDateClick = (date) => {
    if (!date) return;

    if (selecting === 'start') {
      setSelectedStart(date);
      setSelectedEnd(null);
      setSelecting('end');
    } else {
      if (selectedStart && date < selectedStart) {
        setSelectedStart(date);
        setSelectedEnd(selectedStart);
      } else {
        setSelectedEnd(date);
        setSelecting('start');
        setIsOpen(false);
        onChange({
          startDate: selectedStart,
          endDate: date
        });
      }
    }
  };

  const isInRange = (date) => {
    if (!date || !selectedStart) return false;
    if (selectedEnd) {
      return date >= selectedStart && date <= selectedEnd;
    }
    return date.toDateString() === selectedStart.toDateString();
  };

  const isStartDate = (date) => {
    return selectedStart && date?.toDateString() === selectedStart.toDateString();
  };

  const isEndDate = (date) => {
    return selectedEnd && date?.toDateString() === selectedEnd.toDateString();
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handlePreset = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setSelectedStart(start);
    setSelectedEnd(end);
    onChange({ startDate: start, endDate: end });
    setIsOpen(false);
  };

  const presetsList = [
    { label: 'Today', days: 0 },
    { label: 'Yesterday', days: 1 },
    { label: 'Last 7 Days', days: 7 },
    { label: 'Last 30 Days', days: 30 },
    { label: 'This Month', days: 'thisMonth' },
    { label: 'Last Month', days: 'lastMonth' }
  ];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className={`date-range-picker ${className}`} ref={pickerRef}>
      <div className="date-range-input" onClick={() => setIsOpen(!isOpen)}>
        <span className="date-range-icon">📅</span>
        <span className="date-range-text">
          {selectedStart && selectedEnd ? (
            `${formatDate(selectedStart)} - ${formatDate(selectedEnd)}`
          ) : (
            'Select Date Range'
          )}
        </span>
      </div>

      {isOpen && (
        <div className="date-range-dropdown">
          {presets && (
            <div className="presets">
              <h4>Presets</h4>
              {presetsList.map(preset => (
                <button
                  key={preset.label}
                  className="preset-btn"
                  onClick={() => handlePreset(preset.days)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}

          <div className="calendar">
            <div className="calendar-header">
              <button 
                onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}
              >
                ◀
              </button>
              <span>{months[currentMonth.getMonth()]} {currentMonth.getFullYear()}</span>
              <button 
                onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}
              >
                ▶
              </button>
            </div>

            <div className="calendar-weekdays">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                <div key={day} className="weekday">{day}</div>
              ))}
            </div>

            <div className="calendar-days">
              {getMonthData().map((date, index) => (
                <button
                  key={index}
                  className={`calendar-day ${!date ? 'empty' : ''} 
                    ${isStartDate(date) ? 'start' : ''}
                    ${isEndDate(date) ? 'end' : ''}
                    ${isInRange(date) ? 'in-range' : ''}`}
                  onClick={() => handleDateClick(date)}
                  disabled={!date}
                >
                  {date?.getDate()}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;